import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { FLAG_LABEL, MovementFlag, MovementRow } from '../types'
import Mark from './Mark'
import StatusChip from './StatusChip'

/** Data-note flags surfaced inline here; date/counterparty/balance flags live in their own columns. */
const INLINE_FLAGS: MovementFlag[] = [ 'legacy_transfer', 'deprecated_type', 'direction_inferred_from_name' ]

/**
 * The movement's plain-language label, its status badge when it is not a plain completed
 * one, a badge carrying the magnitude of a zero-delta move, inline data-note marks, and
 * the (often long) memo shown inline rather than hidden behind an expander.
 */
export default function MovementCell( { row }: { row: MovementRow } ) {
	const isUnrecognized = row.flags.includes( 'unrecognized_type' )
	const badges = row.flags.filter( ( flag ) => INLINE_FLAGS.includes( flag ) )

	return (
		<Box sx={{ py: 0.5 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
				{isUnrecognized
					// The raw ledger type is no longer shown, so it rides along here — it is the only
					// clue to what an unrecognized movement actually was.
					? <Mark title={`${FLAG_LABEL.unrecognized_type}: ${row.transactionType}`}>Unrecognized type</Mark>
					: <Typography component="span" variant="body2" fontWeight={500}>{row.label}</Typography>}
				<StatusChip status={row.status} />
				{row.direction === 'unchanged' && row.unchangedQuantity !== null && (
					<Box
						component="span"
						sx={{ fontSize: 11, px: 0.75, py: 0.125, borderRadius: 0.5, color: 'text.secondary', bgcolor: 'grey.200' }}
					>
						{row.unchangedQuantity} units · no net change
					</Box>
				)}
				{badges.map( ( flag ) => (
					<Mark key={flag} title={FLAG_LABEL[ flag ]}>{flag.replace( /_/g, ' ' )}</Mark>
				) )}
			</Box>
			{row.memo && (
				<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, whiteSpace: 'normal' }}>
					{row.memo}
				</Typography>
			)}
		</Box>
	)
}
